<template>
  <v-card flat height="calc(100vh - 106px)" class="overflow-y pt-4 pl-4">
    <v-form ref="form">
      <v-container fluid>
        <v-row>
          <v-col cols="11">
            <outlined-block title="Customer">
              <v-container>
                <v-row class="mt-1">
                  <v-col cols="12" sm="4">
                    <v-select label="Customer" :items="customers" item-value="id" item-text="name" outlined dense hide-detail
                              v-model="square.settings.customerForOrders" @change="$emit('saveSettings')" :rules="[fieldRules.required]">
                      <template v-slot:prepend-item>
                        <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="customerCreate=true">
                          <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new customer
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="8" class="text-caption mt-2">
                    PURTRAK customer for shipments created from Square orders
                  </v-col>
                </v-row>
              </v-container>
            </outlined-block>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="11">
            <outlined-block title="Special items">
              <v-container class="mt-4">
                <v-row align="center">
                  <v-col cols="12" sm="6">
                    <v-select :items="items" item-text="name" item-value="id" v-model="square.settings.tipItemId"
                              label="Tip item" outlined dense
                              no-data-text="No items found" :rules="[fieldRules.required]"
                              @change="$emit('saveSettings')">
                      <template v-slot:prepend-item>
                        <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="newItemType='tipItemId';defaultName='Square tip';itemCreate=true">
                          <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new item
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6" class="text-caption mt-n6">
                    PURTRAK item to use for customer tips on orders
                  </v-col>
                </v-row>
              </v-container>
            </outlined-block>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="11">
            <outlined-block title="Invoice">
              <v-container>
                <v-row align="center">
                  <v-col cols="12" sm="8" lg=6>
                    <v-switch label="Create invoice for Square orders" v-model="square.settings.invoice" @change="$emit('saveSettings')"/>
                  </v-col>
                  <v-col cols="8" sm="4" lg="4">
                    <v-autocomplete label="Type" v-model="square.settings.paymentMethodId" :items="paymentMethods" item-value="id" item-text="name"
                                    outlined dense hide-details @change="$emit('saveSettings')" :disabled="!square.settings.invoice" :rules="[fieldRules.required]"/>
                  </v-col>
                </v-row>
                <template v-if="connectedToAccounting && square.settings.invoice">
                  <v-row class="pt-0" align="center">
                    <v-col cols="12" sm="9" lg=6>
                      <v-switch label="Create QuickBooks Online receipt for Square orders" v-model="square.settings.qbo" @change="$emit('saveSettings')"
                                  hint="If you have Square integrated directly with QuickBooks Online, this should be off" persistent-hint/>
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                      <account-picker v-model="square.settings.qboAccountRef" label="Account for payment" :accounts="accounts"
                                      hide-details @change="$emit('saveSettings')" :disabled="!square.settings.qbo" required/>
                    </v-col>
                  </v-row>
                </template>
              </v-container>
            </outlined-block>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="11">
            <outlined-block title="Processing">
              <v-container>
                <v-row align="center" class="mt-1">
                  <v-col cols="6" sm="3" lg="2">
                    <v-select label="Frequency" v-model="square.settings.orderProcessingFrequency"
                              :items="[{text:'Manually',value:'Never'},{text:'Daily',value:'Daily'},{text:'Hourly',value:'Hourly'},{text:'Real time',value:'RealTime'}]"
                              dense outlined hide-details @change="$emit('saveSettings')"/>
                  </v-col>
                  <v-col cols="12" sm="8" lg="6" class="text-caption">Square orders are processed <em>only</em> if they are complete.</v-col>
                </v-row>
                <v-row align="center">
                  <v-col cols="12" sm="6">
                    <v-switch label="Pull products from in-bond inventory" v-model="square.settings.inBond" hide-details
                              @click="checkDraftSetting()"/>
                  </v-col>
                </v-row>
                <v-row align="center" dense>
                  <v-col cols="12" sm="6" lg="4">
                    <v-switch v-model="square.settings.createDraftOrders" label="Create shipments as drafts" @change="$emit('saveSettings')"
                                :hint="draftMessage" persistent-hint :disabled="!!draftMessage"/>
                  </v-col>
                </v-row>
                <v-row align="center" dense>
                  <v-col cols="12" sm="8" lg="6">
                    <v-switch v-model="square.settings.createDraftOrdersOnLowInventory" label="Create shipments as drafts if low on inventory" @change="$emit('saveSettings')"/>
                  </v-col>
                </v-row>
              </v-container>
            </outlined-block>
          </v-col>
        </v-row>
        <v-row kclass="mt-4">
          <v-col cols="11">
            <outlined-block title="Process Square orders now">
              <v-container>
                <v-row align="center" class="mt-1">
                  <v-col cols="8" sm="5" md="4">
                    <date-range hide-details :from.sync="ordersFrom" :to.sync="ordersTo" option="Today"/>
                  </v-col>
                  <v-col cols="7" sm="2">
                    <v-btn color="primary"  @click="processOrders" :loading="loading">Process orders</v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </outlined-block>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <customer-quick-create v-model="customerCreate" @create="newCustomer"/>
    <inventory-items-quick-create v-model="itemCreate" @create="newItem" type="Service" sold :defaultName="defaultName"/>
  </v-card>
</template>

<script>
import CustomerQuickCreate from '@/views/sales/customers/CustomerQuickCreate'
import InventoryItemsQuickCreate from '@/views/inventory/items/InventoryItemsQuickCreate'

export default {
  name: 'AppsSquareOrders',
  components: {CustomerQuickCreate,InventoryItemsQuickCreate},
  props: {
    square: {type:Object, required:false}
  },
  data() {
    return {
      accounts: [],
      paymentMethods: [],
      items: [],
      customers: [],
      customerCreate: false,
      ordersFrom: null,
      ordersTo: null,
      itemCreate: false,
      newItemType: '',
      defaultName: '',
      loading: false
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    draftMessage() {
      if (!this.square.settings.inBond && this.company.settings.lotTrackingForDutyPaid ||
          this.square.settings.inBond && this.company.settings.lotTrackingForProducts)
        return 'Shipments must be draft because of lot tracking';
    }
  },
  async created() {
    const data = await Promise.all([
      this.$store.dispatch('customers', true),
      this.$store.dispatch('items'),
//      this.$lazyListLoad(this.items, 'items2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'},{field:'type',comparator:'ne',value:'Item'}]),
      this.$lazyListLoad(this.paymentMethods, 'paymentMethods2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}])
    ]);
    this.customers = data[0];
    this.items = data[1].filter(item => item.active && item.type !== 'Item');

    if (this.connectedToAccounting) {
      const results = await this.$apiCall(`query AppsSquareOrders {
                                              accounts {
                                                ref,name,type,typeName,currency
                                              }
                                            }`,
                                          {});
      if (!results.error)
        this.accounts = results.data.accounts.filter(account => account.type === 'Bank' || account.type === 'CreditCard' || account.type === 'OtherCurrentAsset');
    }
  },
  mounted() {
    this.validate();
  },
  methods: {
    checkDraftSetting() {
      if (this.draftMessage)
        this.square.settings.createDraftOrders = true;
      this.$emit('saveSettings');
    },
    validate() {
      this.$nextTick(() => this.$refs.form.validate());
    },
    async newCustomer(customer) {
      this.customers = await this.$store.dispatch('customers', true);
      this.square.settings.customerForOrders = customer.id;
      this.$emit('saveSettings');
    },
    async newItem(item) {
      this.items = (await this.$store.dispatch('items')).filter(item => item.type !== 'Item');
      this.square.settings[this.newItemType] = item.id;
      this.$emit('saveSettings');
    },
    async processOrders() {
      this.loading = true;
      const results = await this.$apiCall(`mutation AppsSquareOrders($from:Date!, $to:Date!) {
                                            squareProcessOrdersByDate(from:$from, to:$to) {total,errors}
                                          }`,
                                          {from:this.ordersFrom,to:this.ordersTo},
                                          false, 'square');
      this.loading = false;
      if (!results.error) {
        const orders = results.data.squareProcessOrdersByDate;
        this.$store.commit('setError', {type:orders.errors?'warning':'success',
                                        message:`${orders.total} order(s) processed with ${orders.errors} error(s)${orders.errors?' (See Square log for details)':''}`});
      }
    }
  }
}
</script>
